import { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';
import { PartnersIntegrationsConfig } from './PartnersIntegrationsConfig';

export function Settings() {
  const [parameters, setParameters] = useState<any[]>([]);
  const [mailStatus, setMailStatus] = useState<any | null>(null);
  const [branchConfigs, setBranchConfigs] = useState<any[]>([]);
  const [orgForm, setOrgForm] = useState<any>({});
  const [orgMailStatus, setOrgMailStatus] = useState<any | null>(null);
  const [testOrgMailTo, setTestOrgMailTo] = useState('');
  const [testOrgWhatsAppTo, setTestOrgWhatsAppTo] = useState('');
  const [testOrgMailResult, setTestOrgMailResult] = useState<string | null>(null);
  const [testOrgWhatsAppResult, setTestOrgWhatsAppResult] = useState<string | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [channelForm, setChannelForm] = useState<any>({});
  const [testMailTo, setTestMailTo] = useState('');
  const [testWhatsAppTo, setTestWhatsAppTo] = useState('');
  const [testMailResult, setTestMailResult] = useState<string | null>(null);
  const [testWhatsAppResult, setTestWhatsAppResult] = useState<string | null>(null);
  const [bureauProviders, setBureauProviders] = useState<any[]>([]);
  const [verifications, setVerifications] = useState<any[]>([]);
  const [penaltyRate, setPenaltyRate] = useState('');
  const [legalDays, setLegalDays] = useState('');
  const [bureauDays, setBureauDays] = useState('');
  const [bureauProvider, setBureauProvider] = useState('VERAZ');
  const [validationProviders, setValidationProviders] = useState<any[]>([]);
  const [editingParam, setEditingParam] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'params' | 'bureau' | 'verifications' | 'users' | 'mail' | 'validations' | 'integrations'
  >('params');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Cargar en paralelo sin que un endpoint roto (ej. mail/sucursal) deje vacío Validaciones.
      const results = await Promise.allSettled([
        apiService.listSystemParameters(),
        apiService.getPenaltyRate(),
        apiService.getLegalDaysThreshold(),
        apiService.getBureauAffectationDays(),
        apiService.listBureauProvidersRef(),
        apiService.listEmploymentVerifications(verificationStatus ? { status: verificationStatus } : {}),
        apiService.listBranchNotificationConfigs(),
        apiService.getOrganizationNotificationConfig(),
        apiService.getMailStatus(),
        apiService.listValidationProviders(),
        apiService.getBureauAffectationProvider(),
      ]);

      const value = <T,>(i: number, fallback: T): T =>
        results[i].status === 'fulfilled' ? (results[i] as PromiseFulfilledResult<T>).value : fallback;

      const firstError = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
      if (firstError) {
        const err = firstError.reason;
        setError(err?.response?.data?.message ?? err?.message ?? 'Error parcial al cargar configuración');
      }

      setParameters(value(0, []));
      const penalty = value(1, { monthlyRate: 0 });
      setPenaltyRate(String(penalty.monthlyRate ?? 0));
      const legal = value(2, { days: 0 });
      setLegalDays(String(legal.days ?? 0));
      const bureau = value(3, { days: 0 });
      setBureauDays(String(bureau.days ?? 0));
      setBureauProviders(value(4, []));
      setVerifications(value(5, []));
      const channels = value(6, [] as any[]);
      setBranchConfigs(channels);
      setOrgForm(value(7, {}));
      setOrgMailStatus(value(8, null));
      setValidationProviders(value(9, []));
      const bureauProv = value(10, { provider: 'VERAZ' });
      setBureauProvider(bureauProv.provider ?? 'VERAZ');

      if (!selectedBranchId && channels[0]?.branchId) {
        setSelectedBranchId(channels[0].branchId);
        setChannelForm(channels[0]);
        try {
          setMailStatus(await apiService.getMailStatus(channels[0].branchId));
        } catch {
          /* mail opcional */
        }
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [verificationStatus]);

  const savePenalty = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await apiService.updatePenaltyRate(parseFloat(penaltyRate));
      setPenaltyRate(String(res.monthlyRate));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveLegalDays = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await apiService.updateLegalDaysThreshold(parseInt(legalDays, 10));
      setLegalDays(String(res.days));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBureauDays = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const [daysRes, providerRes] = await Promise.all([
        apiService.updateBureauAffectationDays(parseInt(bureauDays, 10)),
        apiService.updateBureauAffectationProvider(bureauProvider),
      ]);
      setBureauDays(String(daysRes.days));
      setBureauProvider(providerRes.provider);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectBranchChannel = async (branchId: string) => {
    setSelectedBranchId(branchId);
    setTestMailResult(null);
    setTestWhatsAppResult(null);
    try {
      const [config, status] = await Promise.all([
        apiService.getBranchNotificationConfig(branchId),
        apiService.getMailStatus(branchId),
      ]);
      setChannelForm(config);
      setMailStatus(status);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const saveOrgChannelConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        smtpEnabled: orgForm.smtpEnabled,
        smtpProvider: orgForm.smtpProvider || undefined,
        smtpHost: orgForm.smtpHost || undefined,
        smtpPort: orgForm.smtpPort ? Number(orgForm.smtpPort) : undefined,
        smtpSecure: orgForm.smtpSecure,
        smtpUser: orgForm.smtpUser || undefined,
        mailFrom: orgForm.mailFrom || undefined,
        whatsappEnabled: orgForm.whatsappEnabled,
        whatsappProvider: orgForm.whatsappProvider || undefined,
        whatsappAccountSid: orgForm.whatsappAccountSid || undefined,
        whatsappFromNumber: orgForm.whatsappFromNumber || undefined,
      };
      if (orgForm.smtpPass) payload.smtpPass = orgForm.smtpPass;
      if (orgForm.whatsappAuthToken) payload.whatsappAuthToken = orgForm.whatsappAuthToken;
      const saved = await apiService.updateOrganizationNotificationConfig(payload);
      setOrgForm(saved);
      setOrgMailStatus(await apiService.getMailStatus());
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveChannelConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBranchId) return;
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        smtpOverride: channelForm.smtpOverride,
        whatsappOverride: channelForm.whatsappOverride,
        smtpEnabled: channelForm.smtpEnabled,
        smtpProvider: channelForm.smtpProvider || undefined,
        smtpHost: channelForm.smtpHost || undefined,
        smtpPort: channelForm.smtpPort ? Number(channelForm.smtpPort) : undefined,
        smtpSecure: channelForm.smtpSecure,
        smtpUser: channelForm.smtpUser || undefined,
        mailFrom: channelForm.mailFrom || undefined,
        whatsappEnabled: channelForm.whatsappEnabled,
        whatsappProvider: channelForm.whatsappProvider || undefined,
        whatsappAccountSid: channelForm.whatsappAccountSid || undefined,
        whatsappFromNumber: channelForm.whatsappFromNumber || undefined,
      };
      if (channelForm.smtpPass) payload.smtpPass = channelForm.smtpPass;
      if (channelForm.whatsappAuthToken) payload.whatsappAuthToken = channelForm.whatsappAuthToken;
      const saved = await apiService.updateBranchNotificationConfig(selectedBranchId, payload);
      setChannelForm(saved);
      setMailStatus(await apiService.getMailStatus(selectedBranchId));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveParam = async (key: string) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.updateSystemParameter(key, { value: editValue });
      setEditingParam(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Cargando configuración…</div>;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Configuración</h1>
      {error && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {([
          ['params', 'Parámetros operativos'],
          ['bureau', 'Afectaciones buró'],
          ['validations', 'Validaciones'],
          ['integrations', 'Integraciones'],
          ['verifications', 'Verificaciones'],
          ['users', 'Usuarios'],
          ['mail', 'Correo y WhatsApp'],
        ] as const).map(([tab, label]) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'params' && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <form onSubmit={savePenalty} className="bg-white rounded-lg border p-6 space-y-3">
              <h2 className="text-lg font-semibold">Tasa punitoria mensual</h2>
              <p className="text-sm text-gray-500">Decimal (ej. 0.05 = 5% mensual)</p>
              <input type="number" step="0.001" min={0} max={1} value={penaltyRate} onChange={(e) => setPenaltyRate(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">{saving ? 'Guardando…' : 'Guardar'}</button>
            </form>
            <form onSubmit={saveLegalDays} className="bg-white rounded-lg border p-6 space-y-3">
              <h2 className="text-lg font-semibold">Umbral mora → jurídico</h2>
              <p className="text-sm text-gray-500">Días de mora para derivar a jurídico</p>
              <input type="number" min={1} max={3650} value={legalDays} onChange={(e) => setLegalDays(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Guardar</button>
            </form>
          </div>

          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h2 className="font-semibold text-sm">Todos los parámetros del sistema</h2>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clave</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {parameters.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 text-sm font-mono">{p.key}</td>
                    <td className="px-4 py-3 text-sm">
                      {editingParam === p.key ? (
                        <input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
                      ) : (
                        p.value
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.category ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.description ?? '—'}</td>
                    <td className="px-4 py-3 text-right text-sm">
                      {editingParam === p.key ? (
                        <>
                          <button onClick={() => saveParam(p.key)} className="text-green-700 mr-2 hover:underline">Guardar</button>
                          <button onClick={() => setEditingParam(null)} className="text-gray-600 hover:underline">Cancelar</button>
                        </>
                      ) : (
                        <button onClick={() => { setEditingParam(p.key); setEditValue(p.value); }} className="text-blue-700 hover:underline">Editar</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'bureau' && (
        <div className="space-y-6">
          <form onSubmit={saveBureauDays} className="bg-white rounded-lg border p-6 max-w-md space-y-3">
            <h2 className="text-lg font-semibold">Afectación automática</h2>
            <p className="text-sm text-gray-500">Mora mínima y proveedor buró para generar afectaciones PENDING</p>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Días de mora</label>
              <input type="number" min={1} max={3650} value={bureauDays} onChange={(e) => setBureauDays(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Proveedor automático</label>
              <select value={bureauProvider} onChange={(e) => setBureauProvider(e.target.value)} className="w-full px-3 py-2 border rounded-md">
                <option value="VERAZ">Veraz</option>
                <option value="RIESGO_ONLINE">Riesgo OnLine</option>
                <option value="PYP">PyP</option>
                <option value="APICC">Apicc</option>
              </select>
            </div>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Guardar</button>
          </form>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Integración</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bureauProviders.map((p) => (
                  <tr key={p.code}>
                    <td className="px-4 py-3 text-sm font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {p.integrationStatus
                        ? `${p.integrationStatus.mode}${p.integrationStatus.configured ? ' · OK' : ' · pendiente'}`
                        : p.hasIntegration ? 'Manual' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'validations' && (
        <ValidationProvidersConfig
          providers={validationProviders}
          onReload={load}
        />
      )}

      {activeTab === 'integrations' && <PartnersIntegrationsConfig />}

      {activeTab === 'verifications' && (
        <div className="space-y-4">
          <div className="flex gap-3 items-center">
            <label className="text-sm text-gray-600">Estado:</label>
            <select value={verificationStatus} onChange={(e) => setVerificationStatus(e.target.value)} className="px-3 py-2 border rounded-md text-sm">
              <option value="">Todos</option>
              <option value="PENDING">Pendiente</option>
              <option value="VERIFIED">Verificado</option>
              <option value="FAILED">Fallido</option>
            </select>
          </div>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Préstamo</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Empleador</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Creado</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {verifications.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-6 text-gray-500">Sin verificaciones.</td></tr>
                ) : verifications.map((v) => (
                  <tr key={v.id}>
                    <td className="px-4 py-2">{v.customer?.firstName} {v.customer?.lastName}</td>
                    <td className="px-4 py-2 font-mono text-xs">{v.loan?.loanNumber ?? '—'}</td>
                    <td className="px-4 py-2">{v.employerName ?? '—'}</td>
                    <td className="px-4 py-2">{v.status}</td>
                    <td className="px-4 py-2">{String(v.createdAt).slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right">
                      {v.status === 'PENDING' && (
                        <>
                          <button onClick={() => apiService.updateEmploymentVerification(v.id, { status: 'VERIFIED' }).then(load)} className="text-green-700 mr-2 hover:underline text-xs">Verificar</button>
                          <button onClick={() => apiService.updateEmploymentVerification(v.id, { status: 'FAILED' }).then(load)} className="text-red-700 hover:underline text-xs">Rechazar</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white rounded-lg border p-4 text-sm text-gray-600">
          <p>Gestión completa de usuarios y matriz de permisos en <a href="/users" className="text-blue-700 hover:underline">Usuarios y permisos</a>.</p>
        </div>
      )}

      {activeTab === 'mail' && (
        <div className="space-y-8 max-w-3xl">
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Configuración global</h2>
              <p className="text-sm text-gray-500 mt-1">
                Aplica a todas las sucursales. Una sucursal puede sobrescribir correo o WhatsApp con su propia configuración.
              </p>
            </div>

            {orgMailStatus && (
              <p className={`text-sm font-medium ${orgMailStatus.configured ? 'text-green-700' : 'text-amber-700'}`}>
                SMTP global: {orgMailStatus.configured ? 'operativo' : 'pendiente de configuración'}
              </p>
            )}

            <form onSubmit={saveOrgChannelConfig} className="space-y-6">
              <fieldset className="space-y-3 border rounded-md p-4">
                <legend className="text-sm font-semibold px-1">Correo (SMTP)</legend>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!orgForm.smtpEnabled}
                    onChange={(e) => setOrgForm({ ...orgForm, smtpEnabled: e.target.checked })}
                  />
                  Habilitar SMTP global
                </label>
                <select
                  value={orgForm.smtpProvider ?? 'gmail_workspace'}
                  onChange={(e) => setOrgForm({ ...orgForm, smtpProvider: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="gmail_workspace">Google Workspace (smtp.gmail.com)</option>
                  <option value="google_smtp_relay">Google SMTP Relay</option>
                  <option value="custom">SMTP personalizado</option>
                </select>
                {orgForm.smtpProvider === 'custom' && (
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Host" value={orgForm.smtpHost ?? ''} onChange={(e) => setOrgForm({ ...orgForm, smtpHost: e.target.value })} className="px-3 py-2 border rounded-md text-sm" />
                    <input placeholder="Puerto" type="number" value={orgForm.smtpPort ?? 587} onChange={(e) => setOrgForm({ ...orgForm, smtpPort: e.target.value })} className="px-3 py-2 border rounded-md text-sm" />
                  </div>
                )}
                <input placeholder="Usuario SMTP" value={orgForm.smtpUser ?? ''} onChange={(e) => setOrgForm({ ...orgForm, smtpUser: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                <input placeholder={orgForm.smtpPassSet ? 'Contraseña (dejar vacío para no cambiar)' : 'App password / contraseña SMTP'} type="password" value={orgForm.smtpPass ?? ''} onChange={(e) => setOrgForm({ ...orgForm, smtpPass: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                <input placeholder="Remitente (no-reply@creditnow.com.ar)" value={orgForm.mailFrom ?? ''} onChange={(e) => setOrgForm({ ...orgForm, mailFrom: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
              </fieldset>

              <fieldset className="space-y-3 border rounded-md p-4">
                <legend className="text-sm font-semibold px-1">WhatsApp</legend>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!orgForm.whatsappEnabled}
                    onChange={(e) => setOrgForm({ ...orgForm, whatsappEnabled: e.target.checked })}
                  />
                  Habilitar WhatsApp global
                </label>
                <select
                  value={orgForm.whatsappProvider ?? 'twilio'}
                  onChange={(e) => setOrgForm({ ...orgForm, whatsappProvider: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="twilio">Twilio</option>
                  <option value="meta">Meta Cloud API</option>
                </select>
                <input placeholder={orgForm.whatsappProvider === 'meta' ? 'Phone Number ID' : 'Account SID'} value={orgForm.whatsappAccountSid ?? ''} onChange={(e) => setOrgForm({ ...orgForm, whatsappAccountSid: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                <input placeholder={orgForm.whatsappAuthTokenSet ? 'Token (dejar vacío para no cambiar)' : 'Auth Token / Access Token'} type="password" value={orgForm.whatsappAuthToken ?? ''} onChange={(e) => setOrgForm({ ...orgForm, whatsappAuthToken: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                {orgForm.whatsappProvider !== 'meta' && (
                  <input placeholder="Número origen (+54911...)" value={orgForm.whatsappFromNumber ?? ''} onChange={(e) => setOrgForm({ ...orgForm, whatsappFromNumber: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                )}
              </fieldset>

              <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Guardando…' : 'Guardar configuración global'}
              </button>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <form
                className="space-y-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setTestOrgMailResult(null);
                  try {
                    const res = await apiService.testOrganizationMail(testOrgMailTo);
                    setTestOrgMailResult(res.skipped ? 'Omitido — revisar SMTP global' : `Enviado (${res.messageId})`);
                  } catch (err: any) {
                    setTestOrgMailResult(err?.response?.data?.message ?? err.message);
                  }
                }}
              >
                <h3 className="text-sm font-semibold">Probar correo global</h3>
                <input type="email" required placeholder="email@ejemplo.com" value={testOrgMailTo} onChange={(e) => setTestOrgMailTo(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" />
                <button type="submit" className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm">Enviar</button>
                {testOrgMailResult && <p className="text-xs text-gray-600">{testOrgMailResult}</p>}
              </form>
              <form
                className="space-y-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setTestOrgWhatsAppResult(null);
                  try {
                    const res = await apiService.testOrganizationWhatsApp(testOrgWhatsAppTo);
                    setTestOrgWhatsAppResult(res.skipped ? (res.reason ?? 'Omitido') : `Enviado (${res.sid})`);
                  } catch (err: any) {
                    setTestOrgWhatsAppResult(err?.response?.data?.message ?? err.message);
                  }
                }}
              >
                <h3 className="text-sm font-semibold">Probar WhatsApp global</h3>
                <input required placeholder="+54911..." value={testOrgWhatsAppTo} onChange={(e) => setTestOrgWhatsAppTo(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" />
                <button type="submit" className="px-3 py-1.5 bg-green-700 text-white rounded text-sm">Enviar</button>
                {testOrgWhatsAppResult && <p className="text-xs text-gray-600">{testOrgWhatsAppResult}</p>}
              </form>
            </div>
          </div>

          <div className="bg-white rounded-lg border p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Override por sucursal</h2>
              <p className="text-sm text-gray-500 mt-1">
                Por defecto cada sucursal usa la configuración global. Activá el override solo donde necesites valores distintos.
              </p>
            </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
            <select
              value={selectedBranchId}
              onChange={(e) => selectBranchChannel(e.target.value)}
              className="w-full max-w-md px-3 py-2 border rounded-md text-sm"
            >
              <option value="">Seleccionar…</option>
              {branchConfigs.map((c) => (
                <option key={c.branchId} value={c.branchId}>
                  {c.branch?.name ?? c.branchId}
                  {c.smtpConfigured || c.whatsappConfigured ? ' ✓' : ''}
                  {c.smtpOverride || c.whatsappOverride ? ' (override)' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedBranchId && mailStatus && (
            <>
              <p className={`text-sm font-medium ${mailStatus.configured ? 'text-green-700' : 'text-amber-700'}`}>
                SMTP efectivo: {mailStatus.configured ? 'operativo' : 'pendiente'}
                {mailStatus.source === 'organization' && ' (hereda global)'}
                {mailStatus.source === 'branch' && ' (override sucursal)'}
              </p>

              <form onSubmit={saveChannelConfig} className="space-y-6">
                <fieldset className="space-y-3 border rounded-md p-4">
                  <legend className="text-sm font-semibold px-1">Correo (SMTP)</legend>
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={!!channelForm.smtpOverride}
                      onChange={(e) => setChannelForm({ ...channelForm, smtpOverride: e.target.checked })}
                    />
                    Usar configuración propia de correo (sobrescribir global)
                  </label>
                  {!channelForm.smtpOverride ? (
                    <p className="text-sm text-gray-500">Esta sucursal usa el SMTP global.</p>
                  ) : (
                    <>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!channelForm.smtpEnabled}
                      onChange={(e) => setChannelForm({ ...channelForm, smtpEnabled: e.target.checked })}
                    />
                    Habilitar SMTP para esta sucursal
                  </label>
                  <select
                    value={channelForm.smtpProvider ?? 'gmail_workspace'}
                    onChange={(e) => setChannelForm({ ...channelForm, smtpProvider: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="gmail_workspace">Google Workspace (smtp.gmail.com)</option>
                    <option value="google_smtp_relay">Google SMTP Relay</option>
                    <option value="custom">SMTP personalizado</option>
                  </select>
                  {channelForm.smtpProvider === 'custom' && (
                    <div className="grid grid-cols-2 gap-2">
                      <input placeholder="Host" value={channelForm.smtpHost ?? ''} onChange={(e) => setChannelForm({ ...channelForm, smtpHost: e.target.value })} className="px-3 py-2 border rounded-md text-sm" />
                      <input placeholder="Puerto" type="number" value={channelForm.smtpPort ?? 587} onChange={(e) => setChannelForm({ ...channelForm, smtpPort: e.target.value })} className="px-3 py-2 border rounded-md text-sm" />
                    </div>
                  )}
                  <input placeholder="Usuario SMTP" value={channelForm.smtpUser ?? ''} onChange={(e) => setChannelForm({ ...channelForm, smtpUser: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                  <input placeholder={channelForm.smtpPassSet ? 'Contraseña (dejar vacío para no cambiar)' : 'App password / contraseña SMTP'} type="password" value={channelForm.smtpPass ?? ''} onChange={(e) => setChannelForm({ ...channelForm, smtpPass: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                  <input placeholder="Remitente (no-reply@creditnow.com.ar)" value={channelForm.mailFrom ?? ''} onChange={(e) => setChannelForm({ ...channelForm, mailFrom: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                    </>
                  )}
                </fieldset>

                <fieldset className="space-y-3 border rounded-md p-4">
                  <legend className="text-sm font-semibold px-1">WhatsApp</legend>
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={!!channelForm.whatsappOverride}
                      onChange={(e) => setChannelForm({ ...channelForm, whatsappOverride: e.target.checked })}
                    />
                    Usar configuración propia de WhatsApp (sobrescribir global)
                  </label>
                  {!channelForm.whatsappOverride ? (
                    <p className="text-sm text-gray-500">Esta sucursal usa el WhatsApp global.</p>
                  ) : (
                    <>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!channelForm.whatsappEnabled}
                      onChange={(e) => setChannelForm({ ...channelForm, whatsappEnabled: e.target.checked })}
                    />
                    Habilitar WhatsApp para esta sucursal
                  </label>
                  <select
                    value={channelForm.whatsappProvider ?? 'twilio'}
                    onChange={(e) => setChannelForm({ ...channelForm, whatsappProvider: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="twilio">Twilio</option>
                    <option value="meta">Meta Cloud API</option>
                  </select>
                  <input placeholder={channelForm.whatsappProvider === 'meta' ? 'Phone Number ID' : 'Account SID'} value={channelForm.whatsappAccountSid ?? ''} onChange={(e) => setChannelForm({ ...channelForm, whatsappAccountSid: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                  <input placeholder={channelForm.whatsappAuthTokenSet ? 'Token (dejar vacío para no cambiar)' : 'Auth Token / Access Token'} type="password" value={channelForm.whatsappAuthToken ?? ''} onChange={(e) => setChannelForm({ ...channelForm, whatsappAuthToken: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                  {channelForm.whatsappProvider !== 'meta' && (
                    <input placeholder="Número origen (+54911...)" value={channelForm.whatsappFromNumber ?? ''} onChange={(e) => setChannelForm({ ...channelForm, whatsappFromNumber: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                  )}
                    </>
                  )}
                </fieldset>

                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Guardando…' : 'Guardar override de sucursal'}
                </button>
              </form>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <form
                  className="space-y-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setTestMailResult(null);
                    try {
                      const res = await apiService.testBranchMail(selectedBranchId, testMailTo);
                      setTestMailResult(res.skipped ? 'Omitido — revisar SMTP' : `Enviado (${res.messageId})`);
                    } catch (err: any) {
                      setTestMailResult(err?.response?.data?.message ?? err.message);
                    }
                  }}
                >
                  <h3 className="text-sm font-semibold">Probar correo (config efectiva)</h3>
                  <input type="email" required placeholder="email@ejemplo.com" value={testMailTo} onChange={(e) => setTestMailTo(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" />
                  <button type="submit" className="px-3 py-1.5 bg-gray-800 text-white rounded text-sm">Enviar</button>
                  {testMailResult && <p className="text-xs text-gray-600">{testMailResult}</p>}
                </form>
                <form
                  className="space-y-2"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setTestWhatsAppResult(null);
                    try {
                      const res = await apiService.testBranchWhatsApp(selectedBranchId, testWhatsAppTo);
                      setTestWhatsAppResult(res.skipped ? (res.reason ?? 'Omitido') : `Enviado (${res.sid})`);
                    } catch (err: any) {
                      setTestWhatsAppResult(err?.response?.data?.message ?? err.message);
                    }
                  }}
                >
                  <h3 className="text-sm font-semibold">Probar WhatsApp (config efectiva)</h3>
                  <input required placeholder="+54911..." value={testWhatsAppTo} onChange={(e) => setTestWhatsAppTo(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" />
                  <button type="submit" className="px-3 py-1.5 bg-green-700 text-white rounded text-sm">Enviar</button>
                  {testWhatsAppResult && <p className="text-xs text-gray-600">{testWhatsAppResult}</p>}
                </form>
              </div>
            </>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

function ValidationProvidersConfig({
  providers,
  onReload,
}: {
  providers: any[];
  onReload: () => Promise<void> | void;
}) {
  const [drafts, setDrafts] = useState<Record<string, any>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testDoc, setTestDoc] = useState('');
  const [messages, setMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    const next: Record<string, any> = {};
    for (const p of providers) {
      next[p.id] = {
        isActive: p.isActive,
        isMandatory: p.isMandatory,
        blocksOrigination: p.blocksOrigination,
        allowOverride: p.allowOverride,
        integrationMode: p.integrationMode === 'STUB' ? 'API' : p.integrationMode,
        apiUrl: p.apiUrl ?? '',
        gatewayUrl: p.gatewayUrl ?? '',
        apiUser: p.apiUser ?? '',
        apiPassword: '',
        soapMethod: p.soapMethod ?? '',
        timeoutMs: p.timeoutMs ?? 30000,
        clearPassword: false,
      };
    }
    setDrafts(next);
  }, [providers]);

  const updateDraft = (id: string, patch: Record<string, unknown>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const save = async (p: any) => {
    const d = drafts[p.id];
    if (!d) return;
    setSavingId(p.id);
    setMessages((m) => ({ ...m, [p.id]: '' }));
    try {
      const payload: Record<string, unknown> = {
        isActive: d.isActive,
        isMandatory: d.isMandatory,
        blocksOrigination: d.blocksOrigination,
        allowOverride: d.allowOverride,
        integrationMode: d.integrationMode,
        apiUrl: d.apiUrl,
        gatewayUrl: d.gatewayUrl,
        apiUser: d.apiUser,
        soapMethod: d.soapMethod,
        timeoutMs: Number(d.timeoutMs) || 30000,
      };
      if (d.clearPassword) payload.apiPassword = '__CLEAR__';
      else if (d.apiPassword) payload.apiPassword = d.apiPassword;
      await apiService.updateValidationProvider(p.id, payload);
      setMessages((m) => ({ ...m, [p.id]: 'Guardado' }));
      await onReload();
    } catch (e: any) {
      setMessages((m) => ({
        ...m,
        [p.id]: e?.response?.data?.message ?? e.message ?? 'Error al guardar',
      }));
    } finally {
      setSavingId(null);
    }
  };

  const test = async (p: any) => {
    setTestingId(p.id);
    setMessages((m) => ({ ...m, [p.id]: '' }));
    try {
      const res = await apiService.testValidationProvider(p.id, {
        documentNumber: testDoc || undefined,
      });
      const sample = res.sample
        ? ` · muestra: ${res.sample.status}${res.sample.error ? ` (${res.sample.error})` : ''}`
        : '';
      setMessages((m) => ({
        ...m,
        [p.id]: `${res.ok ? 'OK' : 'Falla'}: ${res.detail || res.health?.detail || ''}${sample}`,
      }));
    } catch (e: any) {
      setMessages((m) => ({
        ...m,
        [p.id]: e?.response?.data?.message ?? e.message ?? 'Error al probar',
      }));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border p-4 text-sm text-slate-600 space-y-2">
        <p>
          Configuración interna de los 11 evaluadores. URL, usuario y contraseña/API key se guardan en base de datos
          (la clave se cifra). No se muestran secretos al recargar.
        </p>
        <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          Si un organismo no ofrece API, deje URL vacía y use modo <strong>Manual</strong>, o cargue la URL del
          gateway cuando el proveedor se la entregue — el sistema no inventa resultados en verde.
        </p>
        <div className="flex flex-wrap gap-2 items-end pt-1">
          <div>
            <label className="block text-[11px] uppercase text-slate-500 mb-1">DNI/CUIT para prueba opcional</label>
            <input
              value={testDoc}
              onChange={(e) => setTestDoc(e.target.value)}
              placeholder="Opcional al Probar"
              className="border rounded-md px-3 py-1.5 text-sm w-48"
            />
          </div>
        </div>
      </div>

      {providers.length === 0 ? (
        <div className="bg-white rounded-lg border border-amber-200 p-4 text-sm text-amber-900">
          No se cargaron proveedores de validación. Recargá la página o verificá permisos del módulo Settings.
        </div>
      ) : null}

      {providers.map((p) => {
        const d = drafts[p.id];
        if (!d) return null;
        const open = expanded === p.id;
        const statusLabel = p.adapterConfigured
          ? 'Listo'
          : p.passwordConfigured || p.apiUrl
            ? 'Parcial'
            : 'Sin configurar';
        return (
          <div key={p.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
              onClick={() => setExpanded(open ? null : p.id)}
            >
              <div>
                <p className="font-semibold text-slate-900">
                  {p.name}
                  {p.isMandatory ? ' *' : ''}
                </p>
                <p className="text-xs text-slate-500 font-mono">{p.code}</p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span
                  className={`px-2 py-0.5 rounded ${
                    statusLabel === 'Listo'
                      ? 'bg-emerald-100 text-emerald-800'
                      : statusLabel === 'Parcial'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {statusLabel}
                </span>
                <span className="text-slate-400">{open ? '▲' : '▼'}</span>
              </div>
            </button>

            {open && (
              <div className="border-t border-slate-100 px-4 py-4 space-y-4">
                <div className="flex flex-wrap gap-4 text-sm">
                  {(
                    [
                      ['isActive', 'Activo'],
                      ['isMandatory', 'Obligatorio'],
                      ['blocksOrigination', 'Bloquea originación'],
                      ['allowOverride', 'Permite override'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={Boolean(d[key])}
                        onChange={(e) => updateDraft(p.id, { [key]: e.target.checked })}
                      />
                      {label}
                    </label>
                  ))}
                  <label className="inline-flex items-center gap-2">
                    Modo
                    <select
                      value={d.integrationMode}
                      onChange={(e) => updateDraft(p.id, { integrationMode: e.target.value })}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="MANUAL">Manual</option>
                      <option value="API">API</option>
                      <option value="GATEWAY">Gateway</option>
                    </select>
                  </label>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs text-slate-500 mb-1">URL (endpoint o WSDL)</label>
                    <input
                      value={d.apiUrl}
                      onChange={(e) => updateDraft(p.id, { apiUrl: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Gateway URL (opcional)</label>
                    <input
                      value={d.gatewayUrl}
                      onChange={(e) => updateDraft(p.id, { gatewayUrl: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Método SOAP (si aplica)</label>
                    <input
                      value={d.soapMethod}
                      onChange={(e) => updateDraft(p.id, { soapMethod: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      placeholder="ej. getPersona"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Usuario / CUIT representada</label>
                    <input
                      value={d.apiUser}
                      onChange={(e) => updateDraft(p.id, { apiUser: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      Password / API key
                      {p.passwordConfigured ? (
                        <span className="text-emerald-700 ml-1">(ya configurada)</span>
                      ) : null}
                    </label>
                    <input
                      type="password"
                      value={d.apiPassword}
                      onChange={(e) => updateDraft(p.id, { apiPassword: e.target.value, clearPassword: false })}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      placeholder={p.passwordConfigured ? '•••••••• (dejar vacío para no cambiar)' : ''}
                      autoComplete="new-password"
                    />
                    {p.passwordConfigured && (
                      <label className="mt-1 inline-flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={Boolean(d.clearPassword)}
                          onChange={(e) => updateDraft(p.id, { clearPassword: e.target.checked, apiPassword: '' })}
                        />
                        Borrar password guardada
                      </label>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Timeout (ms)</label>
                    <input
                      type="number"
                      value={d.timeoutMs}
                      onChange={(e) => updateDraft(p.id, { timeoutMs: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      min={1000}
                      max={120000}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    disabled={savingId === p.id}
                    onClick={() => save(p)}
                    className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
                  >
                    {savingId === p.id ? 'Guardando…' : 'Guardar'}
                  </button>
                  <button
                    type="button"
                    disabled={testingId === p.id}
                    onClick={() => test(p)}
                    className="px-4 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50 disabled:opacity-50"
                  >
                    {testingId === p.id ? 'Probando…' : 'Probar'}
                  </button>
                  {messages[p.id] ? (
                    <span className="text-xs text-slate-600 max-w-xl">{messages[p.id]}</span>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

